/**
 * Trade Journal - Phase 6
 * Auto-logged trades with strategy context, ATR data, P&L, and personal notes
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { botAPI } from '../services/api';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import {
  BookOpen, TrendingUp, TrendingDown, DollarSign, Target,
  Filter, Search, ChevronDown, ChevronUp, Shield, Award,
  BarChart3, Calendar, Clock, Edit3, Check, X, ArrowUpRight,
  ArrowDownRight, Zap, RefreshCw, Percent
} from 'lucide-react';

// Strategy color map
const STRATEGY_COLORS = {
  day: { bg: 'bg-accent-orange/10', text: 'text-accent-orange', border: 'border-accent-orange/20', label: 'DAY' },
  swing: { bg: 'bg-accent-blue/10', text: 'text-accent-blue', border: 'border-accent-blue/20', label: 'SWING' },
  longterm: { bg: 'bg-accent-purple/10', text: 'text-accent-purple', border: 'border-accent-purple/20', label: 'LONG' },
  wsb: { bg: 'bg-accent-red/10', text: 'text-accent-red', border: 'border-accent-red/20', label: 'WSB' },
  unknown: { bg: 'bg-bg-tertiary', text: 'text-text-muted', border: 'border-border-color', label: '—' },
};

// Reason badge
const ReasonBadge = ({ reason }) => {
  if (!reason) return null;
  const isStop = reason.includes('stop') || reason.includes('STOP');
  const isTarget = reason.includes('target') || reason.includes('TARGET');
  const isPartial = reason.includes('partial') || reason.includes('PARTIAL');
  const isAI = reason.includes('AI') || reason.includes('score');

  let color = 'text-text-muted bg-bg-tertiary';
  if (isStop) color = 'text-accent-red bg-accent-red/10';
  else if (isTarget) color = 'text-accent-green bg-accent-green/10';
  else if (isPartial) color = 'text-accent-blue bg-accent-blue/10';
  else if (isAI) color = 'text-accent-purple bg-accent-purple/10';

  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${color}`}>
      {reason.length > 30 ? reason.substring(0, 30) + '...' : reason}
    </span>
  );
};

// Stats summary cards
const JournalStats = ({ stats }) => {
  if (!stats) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
      <div className="card p-4">
        <div className="text-text-muted text-xs mb-1">Total Trades</div>
        <div className="font-mono text-2xl font-bold">{stats.totalTrades}</div>
      </div>
      <div className={`card p-4 ${stats.totalPnl >= 0 ? 'border-accent-green/20' : 'border-accent-red/20'}`}>
        <div className="text-text-muted text-xs mb-1">Total P&L</div>
        <div className={`font-mono text-2xl font-bold ${stats.totalPnl >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
          {stats.totalPnl >= 0 ? '+' : ''}${stats.totalPnl.toLocaleString()}
        </div>
      </div>
      <div className="card p-4">
        <div className="text-text-muted text-xs mb-1">Win Rate</div>
        <div className={`font-mono text-2xl font-bold ${stats.winRate >= 50 ? 'text-accent-green' : 'text-accent-red'}`}>
          {stats.winRate}%
        </div>
        <div className="text-text-muted text-xs">{stats.wins}W / {stats.losses}L</div>
      </div>
      <div className="card p-4">
        <div className="text-text-muted text-xs mb-1">Avg Win</div>
        <div className="font-mono text-2xl font-bold text-accent-green">+${stats.avgWin.toFixed(0)}</div>
      </div>
      <div className="card p-4">
        <div className="text-text-muted text-xs mb-1">Avg Loss</div>
        <div className="font-mono text-2xl font-bold text-accent-red">${stats.avgLoss.toFixed(0)}</div>
      </div>
      <div className="card p-4">
        <div className="text-text-muted text-xs mb-1">Total Fees</div>
        <div className="font-mono text-2xl font-bold text-text-secondary">${stats.totalFees.toFixed(2)}</div>
      </div>
    </div>
  );
};

// Strategy breakdown
const StrategyBreakdown = ({ byStrategy }) => {
  if (!byStrategy || Object.keys(byStrategy).length === 0) return null;

  return (
    <div className="card p-5 mb-6">
      <h3 className="text-sm font-semibold text-text-secondary mb-4 flex items-center gap-2">
        <BarChart3 className="w-4 h-4" /> Strategy Performance
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(byStrategy).map(([strategy, data]) => {
          const config = STRATEGY_COLORS[strategy] || STRATEGY_COLORS.unknown;
          const winRate = data.trades > 0 ? ((data.wins / data.trades) * 100).toFixed(0) : 0;
          return (
            <div key={strategy} className={`rounded-xl p-4 ${config.bg} border ${config.border}`}>
              <div className={`text-xs font-bold uppercase mb-2 ${config.text}`}>{config.label}</div>
              <div className={`font-mono text-xl font-bold ${data.pnl >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                {data.pnl >= 0 ? '+' : ''}${data.pnl.toFixed(0)}
              </div>
              <div className="text-text-muted text-xs mt-1">
                {data.trades} closed • {winRate}% WR
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Best/Worst trade highlights
const TradeHighlights = ({ stats }) => {
  if (!stats?.bestTrade && !stats?.worstTrade) return null;

  return (
    <div className="grid grid-cols-2 gap-4 mb-6">
      {stats.bestTrade && (
        <div className="card p-4 border-accent-green/20">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-4 h-4 text-accent-green" />
            <span className="text-xs text-accent-green font-semibold">BEST TRADE</span>
          </div>
          <div className="font-mono text-xl font-bold">{stats.bestTrade.symbol}</div>
          <div className="text-accent-green font-mono">+${stats.bestTrade.pnl.toFixed(2)} ({stats.bestTrade.pnlPercent}%)</div>
          <div className="text-text-muted text-xs mt-1">{stats.bestTrade.strategy}</div>
        </div>
      )}
      {stats.worstTrade && (
        <div className="card p-4 border-accent-red/20">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-4 h-4 text-accent-red" />
            <span className="text-xs text-accent-red font-semibold">WORST TRADE</span>
          </div>
          <div className="font-mono text-xl font-bold">{stats.worstTrade.symbol}</div>
          <div className="text-accent-red font-mono">${stats.worstTrade.pnl.toFixed(2)} ({stats.worstTrade.pnlPercent}%)</div>
          <div className="text-text-muted text-xs mt-1">{stats.worstTrade.strategy}</div>
        </div>
      )}
    </div>
  );
};

// Individual journal entry row
const JournalEntry = ({ entry, onSaveNote }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(entry.userNote || '');
  const isBuy = entry.side === 'buy';
  const isProfit = entry.pnl > 0;
  const stratConfig = STRATEGY_COLORS[entry.strategy] || STRATEGY_COLORS.unknown;

  const handleSave = async () => {
    await onSaveNote(entry._id, note);
    setEditing(false);
  };

  return (
    <div className="card mb-2 overflow-hidden">
      <div 
        className="p-4 cursor-pointer hover:bg-bg-tertiary/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          {/* Side indicator */}
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            isBuy ? 'bg-accent-green/15' : 'bg-accent-red/15'
          }`}>
            {isBuy 
              ? <ArrowUpRight className="w-5 h-5 text-accent-green" />
              : <ArrowDownRight className="w-5 h-5 text-accent-red" />
            }
          </div>

          {/* Symbol & strategy */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-lg font-bold">{entry.symbol}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded ${stratConfig.bg} ${stratConfig.text}`}>
                {stratConfig.label}
              </span>
              {entry.breakevenActive && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-accent-blue/10 text-accent-blue flex items-center gap-1">
                  <Shield className="w-3 h-3" /> BE
                </span>
              )}
              {entry.partialTaken && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-accent-green/10 text-accent-green">½</span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-0.5">
              <span className="text-text-muted text-xs">
                {isBuy ? 'BUY' : 'SELL'} {entry.qty} @ ${entry.price?.toFixed(2)}
              </span>
              <ReasonBadge reason={entry.reason} />
            </div>
          </div>

          {/* Value */}
          <div className="text-right flex-shrink-0">
            <div className="font-mono font-bold">${entry.value?.toFixed(0)}</div>
            {entry.pnl !== null && entry.pnl !== undefined && (
              <div className={`font-mono text-sm ${isProfit ? 'text-accent-green' : 'text-accent-red'}`}>
                {isProfit ? '+' : ''}${entry.pnl.toFixed(2)} ({entry.pnlPercent?.toFixed(1)}%)
              </div>
            )}
          </div>

          {/* Timestamp */}
          <div className="text-right text-text-muted text-xs flex-shrink-0 w-20">
            <div>{new Date(entry.timestamp).toLocaleDateString()}</div>
            <div>{new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
          </div>

          {/* Expand icon */}
          <div className="flex-shrink-0">
            {expanded ? <ChevronUp className="w-4 h-4 text-text-muted" /> : <ChevronDown className="w-4 h-4 text-text-muted" />}
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-border-color pt-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            {entry.entryPrice && (
              <div className="bg-bg-tertiary rounded-lg p-2.5">
                <div className="text-text-muted text-xs">Entry Price</div>
                <div className="font-mono font-semibold">${entry.entryPrice.toFixed(2)}</div>
              </div>
            )}
            {entry.exitPrice && (
              <div className="bg-bg-tertiary rounded-lg p-2.5">
                <div className="text-text-muted text-xs">Exit Price</div>
                <div className="font-mono font-semibold">${entry.exitPrice.toFixed(2)}</div>
              </div>
            )}
            {entry.atr && (
              <div className="bg-bg-tertiary rounded-lg p-2.5">
                <div className="text-text-muted text-xs">ATR</div>
                <div className="font-mono font-semibold">${entry.atr.toFixed(2)}</div>
              </div>
            )}
            {entry.stopPrice && (
              <div className="bg-bg-tertiary rounded-lg p-2.5">
                <div className="text-text-muted text-xs">Stop Price</div>
                <div className="font-mono font-semibold text-accent-red">${entry.stopPrice.toFixed(2)}</div>
              </div>
            )}
            {entry.highWaterMark && (
              <div className="bg-bg-tertiary rounded-lg p-2.5">
                <div className="text-text-muted text-xs">High Water Mark</div>
                <div className="font-mono font-semibold text-accent-blue">${entry.highWaterMark.toFixed(2)}</div>
              </div>
            )}
            {entry.aiScore && (
              <div className="bg-bg-tertiary rounded-lg p-2.5">
                <div className="text-text-muted text-xs">AI Score</div>
                <div className="font-mono font-semibold text-accent-purple">{entry.aiScore}%</div>
              </div>
            )}
            {entry.fees > 0 && (
              <div className="bg-bg-tertiary rounded-lg p-2.5">
                <div className="text-text-muted text-xs">Fees</div>
                <div className="font-mono font-semibold">${entry.fees.toFixed(4)}</div>
              </div>
            )}
            {entry.marketContext && (
              <div className="bg-bg-tertiary rounded-lg p-2.5">
                <div className="text-text-muted text-xs">Market Context</div>
                <div className="font-mono text-xs">
                  {entry.marketContext.trend} • RVOL {entry.marketContext.rvol}x
                </div>
              </div>
            )}
          </div>

          {/* Guru / WSB data */}
          {entry.guruData && (
            <div className="bg-accent-orange/5 border border-accent-orange/20 rounded-lg p-2.5 mb-3 text-xs">
              <span className="text-accent-orange font-semibold">🎯 Guru Pick:</span>{' '}
              {entry.guruData.count} gurus hold — {entry.guruData.names?.join(', ')}
            </div>
          )}
          {entry.wsbData && (
            <div className="bg-accent-red/5 border border-accent-red/20 rounded-lg p-2.5 mb-3 text-xs">
              <span className="text-accent-red font-semibold">🚀 WSB:</span>{' '}
              {entry.wsbData.mentions} mentions, {entry.wsbData.sentiment}% sentiment, risk {entry.wsbData.risk}%
            </div>
          )}

          {/* Personal note */}
          <div className="mt-2">
            {editing ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note about this trade..."
                  className="flex-1 bg-bg-tertiary border border-border-color rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
                />
                <button onClick={handleSave} className="p-2 bg-accent-green/10 text-accent-green rounded-lg hover:bg-accent-green/20">
                  <Check className="w-4 h-4" />
                </button>
                <button onClick={() => setEditing(false)} className="p-2 bg-accent-red/10 text-accent-red rounded-lg hover:bg-accent-red/20">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                className="flex items-center gap-2 text-xs text-text-muted hover:text-text-primary transition-colors"
              >
                <Edit3 className="w-3 h-3" />
                {entry.userNote ? (
                  <span className="italic">"{entry.userNote}"</span>
                ) : (
                  <span>Add note...</span>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Trade Journal Page
const TradeJournal = () => {
  const { hasAlpacaKeys } = useAuth();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterSide, setFilterSide] = useState('all');
  const [filterStrategy, setFilterStrategy] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    if (!hasAlpacaKeys) {
      setLoading(false);
      return;
    }

    try {
      const params = { limit: 200 };
      if (filterSide !== 'all') params.side = filterSide;
      if (filterStrategy !== 'all') params.strategy = filterStrategy;
      if (searchQuery) params.symbol = searchQuery;

      const res = await botAPI.getJournal(params);
      setEntries(res.data?.entries || []);
      setStats(res.data?.stats || null);
    } catch (err) {
      console.error('Failed to fetch journal:', err);
      toast.error('Failed to load trade journal');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [hasAlpacaKeys, filterSide, filterStrategy, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleSaveNote = async (id, note) => {
    try {
      await botAPI.updateJournalNote(id, note);
      setEntries(prev => prev.map(e => e._id === id ? { ...e, userNote: note } : e));
      toast.success('Note saved');
    } catch (err) {
      toast.error('Failed to save note');
    }
  };

  if (!hasAlpacaKeys) {
    return (
      <div className="min-h-screen p-6">
        <div className="card p-12 text-center">
          <BookOpen className="w-16 h-16 text-accent-blue mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Trade Journal</h2>
          <p className="text-text-secondary">Connect your Alpaca account to view your trade journal.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navigation
        title="Trade Journal"
        subtitle="Every trade auto-logged with full context"
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {/* Stats */}
        {!loading && stats && <JournalStats stats={stats} />}

        {/* Strategy breakdown + highlights */}
        {!loading && stats && (
          <>
            <StrategyBreakdown byStrategy={stats.byStrategy} />
            <TradeHighlights stats={stats} />
          </>
        )}

        {/* Filters */}
        <div className="card p-4 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 px-4 py-2 bg-bg-tertiary rounded-xl flex-1 max-w-xs">
              <Search className="w-5 h-5 text-text-muted" />
              <input
                type="text"
                placeholder="Search symbol..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                className="bg-transparent border-none outline-none text-text-primary placeholder:text-text-muted w-full font-mono"
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-text-muted" />
              <select
                value={filterSide}
                onChange={(e) => setFilterSide(e.target.value)}
                className="bg-bg-tertiary border border-border-color rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All Sides</option>
                <option value="buy">Buys</option>
                <option value="sell">Sells</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-text-muted" />
              <select
                value={filterStrategy}
                onChange={(e) => setFilterStrategy(e.target.value)}
                className="bg-bg-tertiary border border-border-color rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All Strategies</option>
                <option value="day">Day Trade</option>
                <option value="swing">Swing</option>
                <option value="longterm">Long Term</option>
                <option value="wsb">WSB</option>
              </select>
            </div>

            <div className="text-text-muted text-sm ml-auto">
              {entries.length} entries
            </div>
          </div>
        </div>

        {/* Entries */}
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="card p-5">
                <div className="h-12 loading-shimmer rounded" />
              </div>
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div className="card p-12 text-center">
            <BookOpen className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">No Journal Entries Yet</h2>
            <p className="text-text-secondary">
              The bot will auto-log every trade here with full context — strategy, ATR levels, P&L, and exit reasons. 
              Entries will appear after the bot executes its next trade.
            </p>
          </div>
        ) : (
          <div>
            {entries.map(entry => (
              <JournalEntry
                key={entry._id}
                entry={entry}
                onSaveNote={handleSaveNote}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TradeJournal;
